import React from "react";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import request from "../../../utilities/requests";
import {
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InfoDialog from "./InfoDialog";
import ConfirmRedirectDialog from "./ConfirmRedirectDialog";
import MessageDialog from "../../../components/MessageDialog";
import Notebook from "./Notebook";
import RefundDialog from "./RefundDialog";
import DealBreakersDialog from "./DealBreakersDialog";
import OrdersDialog from "../../../components/OrdersDialog";
import { AppContext } from "../../../contexts/AppContext";
import AuthContext from "../../../contexts/AuthContext";

import { toast } from "../../../utilities/SnackBarUtils";

import "../events.css";

const MyEvents = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { setActiveEvent, myEvents, setMyEvents, event } =
    React.useContext(AppContext);
  const { socket, setPromptData, listeningForPrompt, setListeningForPrompt } =
    React.useContext(AuthContext);
  const [loaded, setLoaded] = React.useState(false);
  const [showAll, setShowAll] = React.useState(false);
  const [activeEvents, setActiveEvents] = React.useState([]);
  const [completedEvents, setCompletedEvents] = React.useState([]);
  const [showCompleted, setShowCompleted] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [eventUrl, setEventUrl] = React.useState(null);
  const [infoOpen, setInfoOpen] = React.useState(false);
  const [checkInOpen, setCheckInOpen] = React.useState(false);
  const [dialogInfo, setDialogInfo] = React.useState({
    open: false,
    title: "",
    body: "",
    checkInMsg: false,
    page: null,
    eventId: null
  });
  const [checkingIn, setCheckingIn] = React.useState(false);
  const [notebookOpen, setNotebookOpen] = React.useState(false);
  const [selectedEventId, setSelectedEventId] = React.useState(null);
  const [eventCounts, setEventCounts] = React.useState({
    active: 0,
    completed: 0,
  });
  const [refundData, setRefundData] = React.useState({});
  const [refundOpen, setRefundOpen] = React.useState(false);
  const [dealBreakersOpen, setDealBreakersOpen] = React.useState(false);

  React.useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/myevents", title: "MyEvents" });
  }, []);

  React.useEffect(() => {
    const primeData = async () => {
      await getMyEvents();
      getActiveEvent();
      setLoaded(true);
    };
    primeData();
  }, []);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getMyEvents = async () => {
    console.log("retrieving my events");
    const result = await request("events?registered=true");

    if (result.success) {
      setMyEvents([...result.events]);
      const ce = result.events.filter((e) => e.status === "completed");
      setCompletedEvents(ce);
      const ae = result.events.filter((e) => e.status !== "completed");
      setActiveEvents(ae);
    }
  };

  const getActiveEvent = async () => {
    const result = await request("events/activeevent");
    if (!result.success) {
      toast.error(result.error);
      navigate("/events");
    }
    setActiveEvent(result.event);
  };

  const handleClose = () => {
    setOpen(false);
    setEventUrl(null);
  };

  const handleCloseInfo = () => {
    setInfoOpen(false);
  };

  const checkForRefund = async (event) => {
    const result = await request(`ticketing/orders/?eventId=${event._id}`);
    if (!result.success) {
      toast.error(result.error);
      return;
    }
    if (result?.redirectToEventbrite) {
      setOpen(true);
      setEventUrl(result.redirectUrl);
    } else {
      setRefundData({ orders: result?.data?.orders, event });
      setRefundOpen(true);
    }
  };

  const handleOpenLink = () => {
    window.open(eventUrl, "_blank", "noreferrer");
    setOpen(false);
    setEventUrl(null);
  };

  const handleCheckIn = async (event) => {
    setCheckingIn(true);
    if (event?.pairingEventType === "dealbreakers") {
      setDealBreakersOpen(true);
      setCheckingIn(false);
      return;
    }

    const result = await request("events/checkin", "post", {
      eventId: event._id,
    });
    setCheckingIn(false);
    if (!result?.success && result?.incompleteProfile) {
      setDialogInfo({
        ...dialogInfo,
        open: true,
        title: t("checkin.completeprofile.title"),
        body: result.message,
        page: "profile",
        eventId: null
      });
      return;
    } else if (!result.success) {
      setDialogInfo({
        ...dialogInfo,
        open: true,
        title: t("checkin.error.title"),
        body: result.error,
        page: "profile",
        eventId: null
      });
      return;
    }
    setDialogInfo({
      ...dialogInfo,
      open: true,
      title: t("checkin.success.title"),
      body: t("checkin.success.content"),
      checkInMsg: true,
      eventId: event?._id
    });
    await getMyEvents();
    setCheckInOpen(true);
    getActiveEvent();
  };

  const handleCheckInClose = async () => {
    if (dialogInfo.checkInMsg) {
      try {
        const body = {
          eventId: dialogInfo?.eventId,
          trigger: "afterCheckin",
        };
        const result = await request("ads/ad/event", "post", body);
        console.log(result);
      } catch (e) {
        var error = `Error triggering ad check. Error: ${e.message}`;
        console.error(error);
      }
    }
    setDialogInfo({
      ...dialogInfo,
      open: false,
      title: "",
      body: "",
      checkInMsg: false,
      page: null,
      eventId: null
    });
  };

  const formatDate = (date) => {
    let d = new Date(date);
    let dateString = d.toDateString();
    let hours = d.getHours();
    let minutes = d.getMinutes();

    let timeString;
    if (hours > 12 && minutes === 0) {
      timeString = `${hours - 12} PM`;
    } else if (hours > 12 && minutes > 0) {
      timeString = `${hours - 12}:${minutes} PM`;
    } else if (hours <= 12 && minutes === 0) {
      timeString = `${hours} AM`;
    } else if (hours <= 12 && minutes > 0) {
      timeString = `${hours}:${minutes} AM`;
    }

    return `${dateString} @ ${timeString}`;
  };

  const renderCardContent = (eventData) => {
    if (eventData.status === "completed") {
      return (
        <>
          <Typography variant="subtitle2" style={{ color: "#6e0101" }}>
            {formatDate(eventData.date)}
          </Typography>
          <Typography align="center" style={{ marginTop: 10 }}>
            Event Completed
          </Typography>
        </>
      );
    }
    if (!eventData.checkedIn) {
      return (
        <>
          <Typography variant="subtitle2" style={{ color: "#6e0101" }}>
            {formatDate(eventData.date)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {eventData.description}
          </Typography>
        </>
      );
    } else if (eventData.checkedIn && eventData.status !== "active") {
      return (
        <>
          <Typography variant="subtitle2" style={{ color: "#6e0101" }}>
            {formatDate(eventData.date)}
          </Typography>
          {/* <div style={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </div> */}
        </>
      );
    } else if (eventData.checkedIn && eventData.status === "active") {
      if (eventData?._id !== event?._id) {
        console.log("refresh active event");
        getActiveEvent();
      }
      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <CheckCircleOutlineIcon className="event-ready-chk" />
        </div>
      );
    }
  };

  const renderGuestListBtn = () => {
    if (event?.showGuestList && event?.pairingEventType !== "mixer") {
      return (
        <Grid item xs={12}>
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              navigate("/activeevent", { state: { page: "guestList" } })
            }
            fullWidth
          >
            View Guestlist
          </Button>
        </Grid>
      );
    }

    return null;
  };

  const renderButtons = (event) => {
    if (event.status === "scheduled") {
      return (
        <Grid
          item
          xs={6}
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Button onClick={() => setInfoOpen(true)}>
            {t("myevents.info.button")}
          </Button>
        </Grid>
      );
    } else if (
      ["pending", "active"].indexOf(event.status) > -1 &&
      !event.checkedIn
    ) {
      return (
        <Grid item xs={12}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleCheckIn(event)}
            disabled={event.checkedIn ? true : false}
            fullWidth
          >
            {checkingIn ? (
              <CircularProgress size={20} />
            ) : (
              t("myevents.checkin.button")
            )}
          </Button>
        </Grid>
      );
    } else if (event.status === "pending" && event.checkedIn) {
      return (
        <Grid item xs={12}>
          <Button
            // variant="outlined"
            size="small"
            fullWidth
          >
            {t("myevents.startingsoon.label")}
          </Button>
        </Grid>
      );
    } else if (event.status === "active" && event.checkedIn) {
      // setActiveEvent(event);
      let page;
      if (event?.pairingEventType === "mixer") {
        page = "mixer";
      } else {
        page = "speedDating";
      }

      return (
        <>
          <Grid item xs={12}>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate("/activeevent", { state: { page } })}
              fullWidth
            >
              {t("myevents.gotoevent.button")}
            </Button>
          </Grid>
          {renderGuestListBtn()}
        </>
      );
    } else if (event.status === "completed") {
      return (
        <Grid
          item
          xs={12}
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          {event?.pairingEventType !== "friend" ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                navigate(`/eventresults/${event._id}`, { state: { event } })
              }
              fullWidth
            >
              {t("myevents.viewresults.button")}
            </Button>
          ) : null}

          {event?.showNotebook ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSelectedEventId(event._id);
                setNotebookOpen(true);
              }}
              fullWidth
            >
              {t("myevents.viewnotebook.button")}
            </Button>
          ) : null}
        </Grid>
      );
    }
  };

  const renderEvents = () => {
    if (myEvents.length === 0) {
      return (
        <Grid
          item
          style={{
            width: "80%",
            height: "80vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 20,
          }}
        >
          <Typography align="center">
            {t("myevents.noevents.registered")}
          </Typography>
          <Typography align="center">{t("myevents.noevents.home")}</Typography>
          <Typography align="center">
            {t("myevents.noevents.contact")}
          </Typography>
        </Grid>
      );
    }

    if (
      !showCompleted &&
      activeEvents.length === 0 &&
      completedEvents.length > 0
    ) {
      return (
        <Grid
          item
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div className="bounce-icon">
            <ExpandLessIcon
              style={{
                width: 50,
                height: 50,
                color: theme.palette.primary.main,
              }}
            />
          </div>
          <Typography align="center">
            {t("myevents.noactiveevents.label")}
          </Typography>
        </Grid>
      );
    }

    const eventArray = showCompleted ? completedEvents : activeEvents;

    return eventArray.map((event) => {
      return (
        <Grid key={event._id} item style={{ marginBottom: 20 }}>
          <Card sx={{ width: 345 }}>
            <CardMedia
              sx={{ height: 140 }}
              image={event.imageUrl}
              title={event.name}
            />
            <CardContent>
              <Typography gutterBottom variant="h6" component="div">
                {event.name}
              </Typography>
              {renderCardContent(event)}
            </CardContent>
            {/* Inline warning for unassigned tickets */}
            {event.hasAssignableTickets && (
              <div style={{ padding: "0 12px 12px 12px" }}>
                <Alert severity="warning" variant="outlined">
                  You have unassigned tickets. Click <strong>View Order</strong>{" "}
                  to assign them.
                </Alert>
              </div>
            )}
            <Grid container spacing={2} style={{ padding: 12 }}>
              {["active", "pending", "completed"].includes(
                event.status
              ) ? null : (
                <Grid item xs={6}>
                  <Button
                    size="small"
                    onClick={() => checkForRefund(event)}
                    disabled={event.checkedIn ? true : false}
                  >
                    {t("myevents.refundcancel.button")}
                  </Button>
                </Grid>
              )}

              {renderButtons(event)}
            </Grid>
          </Card>
        </Grid>
      );
    });
  };

  if (!loaded) {
    return (
      <div
        style={{
          height: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={80} />
      </div>
    );
  }

  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      // justifyContent="center"
      // spacing={3}
      style={{ marginBottom: 50, paddingBottom: 200, width: "100%" }}
    >
      <Grid item style={{ marginBottom: 20 }}>
        {completedEvents.length > 0 ? (
          <Chip
            label={t("myevents.showcompleted.button")}
            variant={showCompleted ? "" : "outlined"}
            color="primary"
            onClick={() => setShowCompleted(!showCompleted)}
          />
        ) : null}
      </Grid>
      {renderEvents()}
      <ConfirmRedirectDialog
        open={open}
        close={handleClose}
        confirm={handleOpenLink}
      />
      <InfoDialog open={infoOpen} close={handleCloseInfo} />
      {/* <CheckInDialog open={checkInOpen} close={handleCheckInClose} /> */}
      <MessageDialog
        open={dialogInfo.open}
        close={handleCheckInClose}
        title={dialogInfo.title}
        body={dialogInfo.body}
        page={dialogInfo?.page}
      />
      <Notebook
        open={notebookOpen}
        close={() => {
          setNotebookOpen(false);
          setSelectedEventId(null);
        }}
        eventId={selectedEventId}
      />
      <OrdersDialog
        open={refundOpen}
        onClose={() => {
          getMyEvents();
          setRefundOpen(false);
          setRefundData({});
        }}
        data={refundData}
      />
      <DealBreakersDialog
        open={dealBreakersOpen}
        close={() => setDealBreakersOpen(false)}
      />
    </Grid>
  );
};

export default MyEvents;
